import { BadgeType } from 'src/components/ui/Badge/Badge';
import { IconName } from 'src/components/ui/Icon/Icon';
import i18n from 'src/localization/i18n';
import { Partner } from 'src/services/partner';
import dayjs from 'dayjs';
import { UserData } from '../services/user';

export const FulfillmentStateKeys = {
  draft: 'draft',
  open: 'open',
  cancel: 'cancel',
  in_progress: 'in_progress',
  under_investigation: 'under_investigation',
} as const;

/* eslint-disable no-useless-escape */
export const validateEmail = (email: string | null | undefined) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/* eslint-disable no-useless-escape */
export const validateWebsite = (website: string | null | undefined) => {
  if (!website || website.trim() === '') return true;

  const websiteRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  return websiteRegex.test(website.trim());
};

export const validatePassword = (password: string) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"'§$%&\/(){}\[\]=,_=:~*\-+|?\\\/]).+$/;
  return passwordRegex.test(password);
};

/**
 * The portal password policy, mirrored 1:1 from the backend
 * (df_clientportal_api/utility/password_policy.py): at least 8 characters, a
 * lower-case letter, an upper-case letter, a digit and one of the symbols the
 * backend accepts. The set is a whitelist, so a few characters still do not
 * count — `^`, `<`, `>`, `` ` `` and `€` among them.
 *
 * Single source of truth for every password field in the portal — do not inline
 * the regex again somewhere else, or the two copies drift apart.
 */
export const isValidUserPassword = (password: string): boolean => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"'§$%&/()[\]{},_=:~*\-+|?\\\/;#@.]).{8,}$/;
  return regex.test(password);
};

export const validateBarcode = (barcode?: string) => {
  if (!barcode) return false;
  // r"^[A-Z0-9]{10}$"
  const regex = /^[A-Z0-9]{10}$/;
  if (!regex.test(barcode)) return false;
  return true;
};

export const formatDate = (date: any) => {
  if (!date) return '';
  const formatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return formatter.format(new Date(date));
};

export const formatOrderDate = (dateObj: any): string => {
  const date = new Date(dateObj);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (dateObj: any): string => {
  const date = new Date(dateObj);
  return `${date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })}, ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })}`;
};

export const formatTime = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const formatDateAndTime = (input: string, userLang: string): string => {
  const date = new Date(input);
  return date.toLocaleDateString(userLang, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Connector hosts are stored inconsistently (bare host for Shopify,
// full https:// URL for Plentymarkets) — normalize for display only.
export const formatConnectorHost = (host: string | null | undefined): string | null => {
  const trimmed = (host || '').trim();
  if (!trimmed) return null;
  return trimmed.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
};

export const extractTextAndNumbers = (input: string) => {
  const texts: string[] = [];
  const numbers: number[] = [];

  const parts = input.split(/\s+/);

  parts.forEach(part => {
    if (/^\d+$/.test(part)) {
      numbers.push(parseInt(part, 10));
    } else {
      texts.push(part);
    }
  });

  return {
    texts,
    numbers,
  };
};

export const formatPartnerAddress = (partner: Partner, inLines?: boolean) => {
  if (inLines) {
    return `${partner.address.street}<br/>${partner.address.zip_code} ${partner.address.city} | ${partner.address.country_name}`;
  }

  // append all address fields only if they are not empty
  const addressFields = [
    partner.address.street,
    partner.address.zip_code,
    partner.address.city,
    partner.address.country_name,
  ].filter(field => field);

  return addressFields.join(' ');
};

export const resolveBadgeTypeForStatus = (status: string) => {
  switch (status) {
    case 'draft':
      return BadgeType.Neutral;
    case 'inactive':
      return BadgeType.Neutral;
    case 'confirmed':
      return BadgeType.Neutral5;
    case 'waiting_for_availability':
      return BadgeType.Neutral4;
    case 'in_progress':
      return BadgeType.Neutral3;
    case 'ready_for_shipment':
      return BadgeType.Neutral2;
    case 'shipped':
      return BadgeType.Successful;
    case 'out_for_delivery':
      return BadgeType.Neutral2;
    case 'delivered':
      return BadgeType.Successful;
    case 'successful':
      return BadgeType.Successful;
    case 'todo':
      return BadgeType.Neutral4;
    case 'partially_delivered':
      return BadgeType.Warning;
    case 'waiting_for_another_operation':
      return BadgeType.Warning;
    case 'cancel':
      return BadgeType.Error;
    case 'entwurf':
      return BadgeType.Neutral2;
    case 'inaktiv':
      return BadgeType.Neutral;
    case 'bestätigt':
      return BadgeType.Neutral5;
    case 'warten_auf_verfügbarkeit':
      return BadgeType.Neutral4;
    case 'in_bearbeitung':
      return BadgeType.Neutral3;
    case 'bereit_für_versand':
      return BadgeType.Neutral4;
    case 'verschickt':
      return BadgeType.Neutral;
    case 'in_zustellung':
      return BadgeType.Neutral2;
    case 'zugestellt':
      return BadgeType.Successful;
    case 'erfolgreich':
      return BadgeType.Successful;
    case 'teilweise_zugestellt':
      return BadgeType.Neutral;
    case 'warten_auf_weitere_vorgänge':
      return BadgeType.Warning;
    case 'abgebrochen':
      return BadgeType.Error;
    case 'teilweise_retourniert':
      return BadgeType.Warning;
    case 'vollständig_retourniert':
      return BadgeType.Neutral;
    default:
      return BadgeType.Neutral;
  }
};

export const resolveBadgeTypeForTrackingLineStatus = (status: string) => {
  switch (status) {
    case 'in_shipping':
      return BadgeType.Neutral;
    default:
      return BadgeType.Neutral;
  }
};

export function getCountryImageSource(countryCode?: string | null): IconName | undefined {
  switch (countryCode) {
    case 'AT':
      return 'flagAustria';
    case 'Austria':
      return 'flagAustria';
    case 'DE':
      return 'flagGermany';
    case 'Germany':
      return 'flagGermany';

    case 'GE':
      return 'flagGeorgia';
    case 'AF':
      return 'flagAfghanistan';
    case 'PK':
      return 'flagPakistan';
    case 'GH':
      return 'flagGhana';
    case 'PW':
      return 'flagPalau';
    case 'AL':
      return 'flagAlbania';
    case 'GI':
      return 'flagGibraltar';
    case 'PS':
      return 'flagPalestine';
    case 'DZ':
      return 'flagAlgeria';
    case 'GR':
      return 'flagGreece';
    case 'PA':
      return 'flagPanama';
    case 'AS':
      return 'flagAmericanSamoa';
    case 'GL':
      return 'flagGreenland';
    case 'PG':
      return 'flagPapuaNewGuinea';
    case 'AD':
      return 'flagAndorra';
    case 'GD':
      return 'flagGrenada';
    case 'PY':
      return 'flagParaguay';
    case 'AO':
      return 'flagAngola';
    case 'GU':
      return 'flagGuam';
    case 'PE':
      return 'flagPeru';
    case 'AI':
      return 'flagAnguilla';
    case 'GT':
      return 'flagGuatemala';
    case 'PH':
      return 'flagPhilippines';
    case 'AG':
      return 'flagAntiguaAndBarbuda';
    case 'GG':
      return 'flagGuernsey';
    case 'AR':
      return 'flagArgentina';
    case 'GN':
      return 'flagGuinea';
    case 'PL':
      return 'flagPoland';
    case 'AM':
      return 'flagArmenia';
    case 'GW':
      return 'flagGuineaBissau';
    case 'PT':
      return 'flagPortugal';
    case 'AW':
      return 'flagAruba';
    case 'GY':
      return 'flagGuyana';
    case 'PR':
      return 'flagPuertoRico';
    case 'AU':
      return 'flagAustralia';
    case 'HT':
      return 'flagHaiti';
    case 'QA':
      return 'flagQatar';
    case 'AZ':
      return 'flagAzerbaijan';
    case 'HN':
      return 'flagHonduras';
    case 'HK':
      return 'flagHongKong';
    case 'CG':
      return 'flagRepublicOfTheCongo';
    case 'BS':
      return 'flagBahamas';
    case 'HU':
      return 'flagHungary';
    case 'RO':
      return 'flagRomania';
    case 'BH':
      return 'flagBahrain';
    case 'IS':
      return 'flagIceland';
    case 'RU':
      return 'flagRussia';
    case 'IN':
      return 'flagIndia';
    case 'RW':
      return 'flagRwanda';
    case 'BD':
      return 'flagBangladesh';
    case 'ID':
      return 'flagIndonesia';
    case 'BB':
      return 'flagBarbados';
    case 'IR':
      return 'flagIran';
    case 'IQ':
      return 'flagIraq';
    case 'WS':
      return 'flagSamoa';
    case 'BY':
      return 'flagBelarus';
    case 'IE':
      return 'flagIreland';
    case 'SM':
      return 'flagSanMarino';
    case 'BE':
      return 'flagBelgium';
    case 'IM':
      return 'flagIsleOfMan';
    case 'ST':
      return 'flagSaoTomeAndPrince';
    case 'BZ':
      return 'flagBelize';
    case 'IL':
      return 'flagIsrael';
    case 'BJ':
      return 'flagBenin';
    case 'IT':
      return 'flagItaly';
    case 'SA':
      return 'flagSaudiArabia';
    case 'BM':
      return 'flagBermuda';
    case 'CI':
      return 'flagIvoryCoast';
    case 'BT':
      return 'flagBhutan';
    case 'JM':
      return 'flagJamaica';
    case 'SN':
      return 'flagSenegal';
    case 'BO':
      return 'flagBolivia';
    case 'JP':
      return 'flagJapan';
    case 'RS':
      return 'flagSerbia';
    case 'JE':
      return 'flagJersey';
    case 'SC':
      return 'flagSeychelles';
    case 'BA':
      return 'flagBosniaAndHerzegovina';
    case 'JO':
      return 'flagJordan';
    case 'SL':
      return 'flagSierraLeone';
    case 'BW':
      return 'flagBotswana';
    case 'KZ':
      return 'flagKazakhstan';
    case 'SG':
      return 'flagSingapore';
    case 'BR':
      return 'flagBrazil';
    case 'KE':
      return 'flagKenya';
    case 'KI':
      return 'flagKiribati';
    case 'IO':
      return 'flagBritishIndianOceanTerritory';
    case 'SK':
      return 'flagSlovakia';
    case 'KW':
      return 'flagKuwait';
    case 'SI':
      return 'flagSlovenia';
    case 'BN':
      return 'flagBrunei';
    case 'KG':
      return 'flagKyrgyzstan';
    case 'SB':
      return 'flagSolomonIslands';
    case 'BG':
      return 'flagBulgaria';
    case 'LA':
      return 'flagLaos';
    case 'SO':
      return 'flagSomalia';
    case 'BF':
      return 'flagBurkinaFaso';
    case 'LV':
      return 'flagLatvia';
    case 'BI':
      return 'flagBurundi';
    case 'LB':
      return 'flagLebanon';
    case 'ZA':
      return 'flagSouthAfrica';
    case 'KH':
      return 'flagCambodia';
    case 'LS':
      return 'flagLesotho';
    case 'KR':
      return 'flagSouthKorea';
    case 'CM':
      return 'flagCameroon';
    case 'LR':
      return 'flagLiberia';
    case 'SS':
      return 'flagSouthSudan';
    case 'CA':
      return 'flagCanada';
    case 'LY':
      return 'flagLibya';
    case 'ES':
      return 'flagSpain';
    case 'LI':
      return 'flagLiechtenstein';
    case 'LK':
      return 'flagSriLanka';
    case 'CV':
      return 'flagCapeVerde';
    case 'LT':
      return 'flagLithuania';
    case 'KY':
      return 'flagCaymanIslands';
    case 'LU':
      return 'flagLuxembourg';
    case 'CF':
      return 'flagCentralAfricanRepublic';
    case 'MO':
      return 'flagMacao';
    case 'MG':
      return 'flagMadagascar';
    case 'SD':
      return 'flagSudan';
    case 'TD':
      return 'flagChad';
    case 'SR':
      return 'flagSuriname';
    case 'CL':
      return 'flagChile';
    case 'MW':
      return 'flagMalawi';
    case 'SZ':
      return 'flagSwaziland';
    case 'CN':
      return 'flagChina';
    case 'MY':
      return 'flagMalaysia';
    case 'SE':
      return 'flagSweden';
    case 'MV':
      return 'flagMaldives';
    case 'CH':
      return 'flagSwitzerland';
    case 'CO':
      return 'flagColombia';
    case 'ML':
      return 'flagMali';
    case 'SY':
      return 'flagSyria';
    case 'KM':
      return 'flagComoros';
    case 'MT':
      return 'flagMalta';
    case 'TW':
      return 'flagTaiwan';
    case 'CK':
      return 'flagCookIslands';
    case 'TJ':
      return 'flagTajikistan';
    case 'MQ':
      return 'flagMartinique';
    case 'TZ':
      return 'flagTanzania';
    case 'CR':
      return 'flagCostaRica';
    case 'MR':
      return 'flagMauritania';
    case 'TH':
      return 'flagThailand';
    case 'HR':
      return 'flagCroatia';
    case 'MU':
      return 'flagMauritius';
    case 'CU':
      return 'flagCuba';
    case 'TG':
      return 'flagTogo';
    case 'MX':
      return 'flagMexico';
    case 'TK':
      return 'flagTokelau';
    case 'CY':
      return 'flagCyprus';
    case 'FM':
      return 'flagMicronesia';
    case 'TO':
      return 'flagTonga';
    case 'CZ':
      return 'flagCzechRepublic';
    case 'MD':
      return 'flagMoldova';
    case 'CD':
      return 'flagDemocraticRepublicOfCongo';
    case 'MC':
      return 'flagMonaco';
    case 'TT':
      return 'flagTrinidadAndTobago';
    case 'DK':
      return 'flagDenmark';
    case 'MN':
      return 'flagMongolia';
    case 'TN':
      return 'flagTunisia';
    case 'DJ':
      return 'flagDjibouti';
    case 'ME':
      return 'flagMontenegro';
    case 'TR':
      return 'flagTurkey';
    case 'DM':
      return 'flagDominica';
    case 'MS':
      return 'flagMontserrat';
    case 'TM':
      return 'flagTurkmenistan';
    case 'DO':
      return 'flagDominicanRepublic';
    case 'MA':
      return 'flagMorocco';
    case 'TL':
      return 'flagEastTimor';
    case 'MZ':
      return 'flagMozambique';
    case 'TV':
      return 'flagTuvalu';
    case 'EC':
      return 'flagEcuador';
    case 'MM':
      return 'flagMyanmar';
    case 'UG':
      return 'flagUganda';
    case 'EG':
      return 'flagEgypt';
    case 'NA':
      return 'flagNamibia';
    case 'UA':
      return 'flagUkraine';
    case 'SV':
      return 'flagElSalvador';
    case 'AE':
      return 'flagUnitedArabEmirates';
    case 'NR':
      return 'flagNauru';
    case 'GB':
      return 'flagUnitedKingdom';
    case 'GQ':
      return 'flagEquatorialGuinea';
    case 'NP':
      return 'flagNepal';
    case 'ER':
      return 'flagEritrea';
    case 'NL':
      return 'flagNetherlands';
    case 'US':
      return 'flagUnitedStates';
    case 'EE':
      return 'flagEstonia';
    case 'NZ':
      return 'flagNewZealand';
    case 'UY':
      return 'flagUruguay';
    case 'ET':
      return 'flagEthiopia';
    case 'NI':
      return 'flagNicaragua';
    case 'UZ':
      return 'flagUzbekistán';
    case 'NE':
      return 'flagNiger';
    case 'VU':
      return 'flagVanuatu';
    case 'NG':
      return 'flagNigeria';
    case 'VA':
      return 'flagVaticanCity';
    case 'FO':
      return 'flagFaroeIslands';
    case 'NU':
      return 'flagNiue';
    case 'VE':
      return 'flagVenezuela';
    case 'FJ':
      return 'flagFiji';
    case 'NF':
      return 'flagNorfolkIsland';
    case 'VN':
      return 'flagVietnam';
    case 'FI':
      return 'flagFinland';
    case 'KP':
      return 'flagNorthKorea';
    case 'FR':
      return 'flagFrance';
    case 'PF':
      return 'flagFrenchPolynesia';
    case 'YE':
      return 'flagYemen';
    case 'GA':
      return 'flagGabon';
    case 'NO':
      return 'flagNorway';
    case 'ZM':
      return 'flagZambia';
    case 'OM':
      return 'flagOman';
    case 'ZW':
      return 'flagZimbabwe';
    case 'GM':
      return 'flagGambia';

    default:
      return undefined;
  }
}

export const getAvailableLanguages = () => {
  const languages = [
    { value: 'en_US', label: 'Englisch' },
    { value: 'de_DE', label: 'Deutsch' },
    { value: 'it_IT', label: 'Italienisch' },
    { value: 'es_ES', label: 'Spanisch' },
    { value: 'zh_CN', label: 'Chinesisch (vereinfacht)' },
  ];

  return languages;
};

export const getCurrencyOptions = () => {
  const languages = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
  ];

  return languages;
};

export const getPaginationOptions = () => [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '30', label: '30' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

export enum Status {
  DRAFT = 'draft',
  UNDER_INVESTIGATION = 'under_investigation',
  OPEN = 'open',
  CONFIRMED = 'confirmed',
  WAITING_FOR_AVAILABILITY = 'waiting_for_availability',
  IN_PROGRESS = 'in_progress',
  NOT_AVAILABLE = 'not_available',
  READY_FOR_SHIPMENT = 'ready_for_shipment',
  SHIPPED = 'shipped',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  PARTIALLY_DELIVERED = 'partially_delivered',
  WAITING_FOR_ANOTHER_OPERATION = 'waiting_for_another_operation',
  CANCEL = 'cancel',
}

export const getStatusOptions = () => {
  return Object.values(Status).map(status => ({
    value: status,
    label: status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase()),
  }));
};

export const getOperatorTranslation = (operator: string) => {
  switch (operator) {
    case '=':
      return i18n.t('operators.equals');
    case '!=':
      return i18n.t('operators.not_equals');
    case '>':
      return i18n.t('operators.greater');
    case '>=':
      return i18n.t('operators.greater_equals');
    case '<':
      return i18n.t('operators.less');
    case '<=':
      return i18n.t('operators.less_equals');
    case 'ilike':
      return i18n.t('operators.contains');
    case 'not ilike':
      return i18n.t('operators.not_contains');
    default:
      return operator;
  }
};

/**
 * Format string for the filter label
 * @param str - string to format
 * @returns - formatted string
 */
export const formatFilterKey = (str: string, modelId?: string) => {
  const selectionValue = str.replace(/^df_/, '').replace(/\b\w/g, char => char.toLowerCase());
  if (modelId) {
    const scopedKey = `filter.${modelId.replace(/\./g, '_')}.${selectionValue}`;
    if (i18n.exists(scopedKey)) return i18n.t(scopedKey);
  }
  return i18n.t(`filter.${selectionValue}`);
};

export const formatConditionDisplayValue = (value: string): string => {
  if (!value) return value;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value.split('T')[0];
  const key = `filter.${value.replace(/^df_/, '').replace(/\b\w/g, char => char.toLowerCase())}`;
  return i18n.exists(key) ? i18n.t(key) : value;
};

/**
 * Format string for the filter label of badges
 * @param str - string to format
 * @returns - formatted string
 */
export const formatBadgeFilterLabel = (str: string) => {
  return str
    .replace(/^df_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const getAvailableWeightUnits = () => {
  const units = [{ value: 'Gr', label: 'Gram' }];
  return units;
};

export const getAvailableDimensionUnits = () => {
  const units = [{ value: 'Cm', label: 'Centimeter' }];
  return units;
};

export const getLangCode = (language: string = 'EN') => {
  const langMap: Record<string, string> = {
    EN: 'en_US',
    DE: 'de_DE',
    IT: 'it_IT',
    ES: 'es_ES',
    ZH: 'zh_CN',
  };
  return langMap[language] || 'en_US';
};

export const transformPermissionsToRoles = (t: any, permissions: any) => {
  const roles: Array<{ category: string; label: string; level: 'none' | 'read' | 'read_write' }> = [];

  const categoryMapping = [
    { key: 'outbound', category: 'order', label: t('navbar.outbound') },
    { key: 'products', category: 'stock', label: t('returns.products') },
    { key: 'returns', category: 'return', label: t('navbar.returns') },
    { key: 'carrier', category: 'carrier', label: t('general.carrier') },
    { key: 'integrations', category: 'integration', label: t('navbar.integrations') },
    { key: 'cases', category: 'cases', label: t('navbar.cases'), minLevel: 'read' as const },
  ];

  categoryMapping.forEach(({ key, category, label, minLevel }) => {
    let level: 'none' | 'read' | 'read_write' = minLevel ?? 'none';

    if (permissions[`${key}_admin`]) {
      level = 'read_write';
    } else if (permissions[`${key}_view`]) {
      level = 'read';
    }

    roles.push({ category, label, level });
  });

  return roles;
};

export const mapRolesToPermissions = (roles: UserData['roles'] = []) => {
  const getLevel = (category: string) => roles.find(r => r.category === category)?.level;
  const hasRead = (category: string) => ['read', 'read_write'].includes(getLevel(category) || '');
  const hasWrite = (category: string) => getLevel(category) === 'read_write';

  return {
    outbound_view: hasRead('order'),
    outbound_admin: hasWrite('order'),
    products_view: hasRead('stock'),
    products_admin: hasWrite('stock'),
    returns_view: hasRead('return'),
    returns_admin: hasWrite('return'),
    carrier_view: hasRead('carrier'),
    carrier_admin: hasWrite('carrier'),
    integrations_view: hasRead('integrations'),
    integrations_admin: hasWrite('integrations'),
    cases_view: true,
    cases_admin: hasWrite('cases'),
  };
};

export const removeEmptyValues = (input: any) => {
  if (!input || typeof input !== 'object') return input;

  return Object.entries(input).reduce((acc: Record<string, any>, [key, value]) => {
    if (value && typeof value === 'object') {
      const nested = removeEmptyValues(value);
      if (Object.keys(nested).length) acc[key] = nested;
    } else if (value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const buildDomainString = (domain?: { key: string; value: string; operator: string }[]) => {
  if (!domain || domain.length === 0) return null;

  const domainFilters = domain.map(({ key, value, operator }) => `("${key}", "${operator}", "${value}")`);

  if (domainFilters.length === 1) {
    return `[${domainFilters[0]}]`;
  }

  const domainParts: string[] = [];

  for (let i = 0; i < domainFilters.length; i += 1) {
    if (i < domainFilters.length - 1) {
      domainParts.push('"|"');
    }
    domainParts.push(domainFilters[i]);
  }

  return `[${domainParts.join(',')}]`;
};

/**
 * Combines mandatory AND filters with an optional group of OR filters into a
 * single Odoo prefix-notation domain string.
 *
 * The OR filters are joined exactly like {@link buildDomainString} (a "|"
 * chain); the result is then AND-ed with every mandatory filter. Use this where
 * a hard constraint must always hold regardless of the user's search (e.g. only
 * confirmed products) — something {@link buildDomainString} cannot express on
 * its own because it only OR-joins its entries.
 */
export const buildAndOrDomain = (
  andFilters?: { key: string; value: string; operator: string }[],
  orFilters?: { key: string; value: string; operator: string }[],
) => {
  const andParts = (andFilters ?? []).map(({ key, value, operator }) => `("${key}", "${operator}", "${value}")`);

  const orStr = buildDomainString(orFilters);
  const orInner = orStr ? orStr.slice(1, -1) : null; // strip the surrounding [ ]

  const segments = [...andParts];
  if (orInner) segments.push(orInner);

  if (segments.length === 0) return null;

  // (n - 1) leading "&" operators AND all segments together in prefix notation.
  const leadingAnds = '"&",'.repeat(segments.length - 1);
  return `[${leadingAnds}${segments.join(',')}]`;
};

export const getAvailableTimePeriods = (t: any) => {
  const timePeriods = [
    { label: t('reports.time_period_days', { period: 7 }), value: 'last_7_days' },
    { label: t('reports.time_period_days', { period: 30 }), value: 'last_30_days' },
    { label: t('reports.time_period_days', { period: 60 }), value: 'last_60_days' },
    { label: t('reports.time_period_days', { period: 90 }), value: 'last_90_days' },
    { label: t('reports.custom'), value: 'custom' },
  ];

  return timePeriods;
};

export const calculateDatesFromPeriod = (period: string) => {
  const now = dayjs();
  let startDate = now;

  switch (period) {
    case 'last_7_days':
      startDate = now.subtract(7, 'day');
      break;
    case 'last_30_days':
      startDate = now.subtract(30, 'day');
      break;
    case 'last_60_days':
      startDate = now.subtract(60, 'day');
      break;
    case 'last_90_days':
      startDate = now.subtract(90, 'day');
      break;
    default:
      startDate = now.subtract(7, 'day');
  }

  return {
    start_date: startDate.format('YYYY-MM-DD'),
    end_date: now.format('YYYY-MM-DD'),
  };
};

export const getTriggerCategoryLabel = (category: string): string => {
  return i18n.t(`workflows.trigger_category.${category}`, { defaultValue: category });
};

export const getIconForTriggerCategory = (category: string): IconName => {
  switch (category) {
    case 'outbound':
      return 'local-shipping';
    case 'inbound':
      return 'inbound';
    case 'inventory':
      return 'inventory';
    case 'returns':
      return 'inbound';
    case 'integrations':
      return 'integrations';
    default:
      return 'local-shipping';
  }
};

export const getColorForTriggerCategory = (category: string): string => {
  switch (category) {
    case 'outbound':
      return 'var(--status-neutral-2-full)';
    case 'inbound':
    case 'inventory':
      return 'var(--status-neutral-4-full)';
    case 'returns':
      return 'var(--status-neutral-3-full)';
    case 'integrations':
      return 'var(--status-neutral-5-full)';
    default:
      return 'var(--status-neutral-2-full)';
  }
};

export const getActionIcon = (actionType: string): IconName => {
  switch (actionType.trim()) {
    case 'mail':
    case 'Sende E-Mail':
    case 'Send Mail':
      return 'send';
    case 'notification':
    case 'Sende Benachrichtigung':
    case 'Send Notification':
      return 'notifications';
    case 'case':
    case 'Case Erstellen':
    case 'Create Case':
      return 'note';
    case 'product':
    case 'Produkt beilegen':
    case 'Enclose product':
      return 'package';
    default:
      return 'package';
  }
};

export const getActionNodeType = (actionType: string) => {
  switch (actionType.trim()) {
    case 'Sende E-Mail':
      return 'emailBuilder';
    case 'Sende Benachrichtigung':
      return 'notificationBuilder';
    case 'Case Erstellen':
      return 'caseBuilder';
    case 'Produkt beilegen':
      return 'productBuilder';
    case 'Send Mail':
      return 'emailBuilder';
    case 'Send Notification':
      return 'notificationBuilder';
    case 'Create Case':
      return 'caseBuilder';
    case 'Enclose product':
      return 'productBuilder';
    default:
      return 'emailBuilder';
  }
};
// maxFractionDigits defaults to 2, which suits money and the cm/kg figures.
// Pass a higher value for quantities the backend stores more precisely — a
// product volume is m³ with digits=(5, 4), so two digits would round a
// 30x20x10 cm product (0.006 m³) up to 0.01 and a smaller one down to 0.
export const formatAmount = (value: number, lang: string | null, maxFractionDigits = 2): string => {
  const raw = lang ?? 'en';
  const locale = raw.includes('_') ? raw.replace('_', '-') : raw;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
    useGrouping: true,
  }).format(value);
};

export function resolveInvoiceBadgeType(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'successful';
    case 'unpaid':
      return 'cancel';
    case 'overdue':
      return 'inactive';
    default:
      return 'inactive';
  }
}

const ACTION_LABEL_TO_KEY: Record<string, string> = {
  'Send Mail': 'mail',
  'Sende E-Mail': 'mail',
  'Send Notification': 'notification',
  'Sende Benachrichtigung': 'notification',
  'Create Case': 'case',
  'Case Erstellen': 'case',
  'Enclose product': 'product',
  'Produkt beilegen': 'product',
};

export const formatActionTypeKey = (actionType: string): string => {
  const trimmed = actionType.trim();
  return ACTION_LABEL_TO_KEY[trimmed] || trimmed;
};

// The backend stores action_type as a display string (localized when the
// workflow was created, e.g. "Create Case"), so we normalise the known
// variants — short keys and the DE/EN labels — to the current locale.
// Mirrors the variant set handled by getActionIcon.
export const getActionLabel = (actionType: string): string => {
  switch (actionType?.trim()) {
    case 'mail':
    case 'Sende E-Mail':
    case 'Send Mail':
      return i18n.t('workflows.send_email');
    case 'notification':
    case 'Sende Benachrichtigung':
    case 'Send Notification':
      return i18n.t('workflows.send_notification');
    case 'case':
    case 'Case Erstellen':
    case 'Create Case':
      return i18n.t('workflows.create_case');
    case 'product':
    case 'Produkt beilegen':
    case 'Enclose product':
      return i18n.t('workflows.enclose_product');
    default:
      return actionType;
  }
};

export const formatConditionValue = (condition: string) => {
  switch (condition.toLowerCase()) {
    case 'in liste':
      return 'in';
    case 'ist':
      return '=';
    case 'ist nicht':
      return '!=';
    case 'in list':
      return 'in';
    case 'is':
      return '=';
    case 'is not':
      return '!=';
    default:
      return condition;
  }
};

// Maps attribute field_type (from /workflows/conditions/attributes) to the
// df.workflow.condition.value_type Selection keys accepted by the BE:
// string | int | float | bool | list
export const mapFieldTypeToValueType = (fieldType: string | undefined, value: string | undefined): string => {
  switch (fieldType) {
    case 'integer':
      return 'int';
    case 'float':
      return 'float';
    case 'boolean':
      return 'bool';
    case 'selection':
      // selection with real options is a string; selection without options renders
      // as Yes/No (value === 'True' | 'False'), which the BE stores as bool.
      return value === 'True' || value === 'False' ? 'bool' : 'string';
    default:
      return 'string';
  }
};

export const reverseFormatConditionValue = (condition: string, language: 'de' | 'en' = 'de') => {
  switch (condition.toLowerCase()) {
    case 'in':
      return language === 'de' ? 'in Liste' : 'in list';
    case '=':
      return language === 'de' ? 'ist' : 'is';
    case '!=':
      return language === 'de' ? 'ist nicht' : 'is not';
    default:
      return condition;
  }
};

/**
 * Initials for an avatar chip: first letter of the first word plus first letter
 * of the last word, e.g. "Anna Weber" -> "AW", "Anna" -> "A".
 *
 * Duplicates the private helper in Nav/NavItemUser; that one should be switched
 * over to this export the next time the nav is touched.
 */
export const getInitials = (name?: string | null): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
};

/** Special characters accepted by {@link isValidUserPassword}. Kept in sync with that regex. */
const PASSWORD_SPECIAL_CHARACTERS = /[!"'§$%&/()[\]{},_=:~*\-+|?\\\/;#@.]/;

/** 0 = empty, 3 = every rule of the password policy satisfied. */
export type PasswordStrength = 0 | 1 | 2 | 3;

/**
 * Score for the three-segment strength meter next to the initial password field.
 *
 * The policy has *four* criteria (length, mixed case, digit, symbol) but the
 * meter has three segments, so they are folded like this:
 *
 *   0 criteria / empty -> 0   nothing typed yet, the hint lists the requirements
 *   1-2 criteria       -> 1   "weak", more than one thing is missing
 *   3 criteria         -> 2   "almost", exactly one thing is missing
 *   4 criteria         -> 3   accepted
 *
 * Folding the low end (rather than the top) is what keeps the invariant that
 * matters: a full meter is reached **exactly** when {@link isValidUserPassword}
 * returns true. A meter reading "strong" while the backend answers 400 would be
 * a lie — and that is precisely the bug this mapping exists to prevent.
 */
export const getPasswordStrength = (password: string): PasswordStrength => {
  if (!password) return 0;
  let criteriaMet = 0;
  if (password.length >= 8) criteriaMet += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) criteriaMet += 1;
  if (/\d/.test(password)) criteriaMet += 1;
  if (PASSWORD_SPECIAL_CHARACTERS.test(password)) criteriaMet += 1;

  if (criteriaMet === 4) return 3;
  if (criteriaMet === 3) return 2;
  return 1;
};

export const detectLanguageFromConditions = (conditions: any[]) => {
  const hasGermanLabels = conditions.some(
    condition => condition.label === 'ist' || condition.label === 'ist nicht' || condition.label === 'in Liste',
  );

  if (hasGermanLabels) return 'de';

  const hasEnglishLabels = conditions.some(
    condition => condition.label === 'is' || condition.label === 'is not' || condition.label === 'in list',
  );

  if (hasEnglishLabels) return 'en';

  return 'de';
};
